import React, { useState } from "react";
import { Download } from "lucide-react";
import { StaffShell } from "@/components/shells";
import { Panel, Spinner, Tag, EmptyState, Input, Btn } from "@/components/kit";
import { useAsync } from "@/lib/hooks";
import { api } from "@/lib/api";
import { fmtDateTime, ROLE_LABEL } from "@/lib/vda";

const ACTION_META = {
  confirmation_intake: { label: "Received", tone: "muted" },
  confirmation_matched: { label: "Matched", tone: "green" },
  confirmation_rejected: { label: "Rejected", tone: "red" },
  confirmation_applied: { label: "Applied", tone: "green" },
  marked_submitted: { label: "Marked submitted", tone: "clay" },
};

// CONF-AUD-001 — supervisor read-only trail over EXISTING audit_events + confirmation/
// booking records. No new audit table; nothing is mutable here.
export default function ConfirmationAudit() {
  const { data, loading } = useAsync(() => api.confAudit(300), []);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const events = (data?.events || []).filter((e) => {
    if (action && e.action !== action) return false;
    const day = (e.created_at || "").slice(0, 10);
    if (from && day < from) return false;
    if (to && day > to) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [e.registration, e.vda_reference, e.booking_reference, e.garage, e.customer, e.actor]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  });

  // Weekly throughput KPIs — computed from the full loaded set (independent of the
  // action/search/date filters) so supervisors always see "this week" at a glance.
  const weekStart = (() => { const d = new Date(); const off = (d.getDay() + 6) % 7; d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - off); return d; })();
  const wk = (data?.events || []).filter((e) => e.created_at && new Date(e.created_at) >= weekStart);
  const kpi = {
    received: wk.filter((e) => e.action === "confirmation_intake").length,
    applied: wk.filter((e) => e.action === "confirmation_applied").length,
    rejected: wk.filter((e) => e.action === "confirmation_rejected").length,
  };

  const exportCsv = () => {
    const cols = ["When", "Action", "Registration", "Booking/VDA ref", "Garage/Customer", "Result", "Staff", "Role"];
    const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
    const lines = [cols.join(",")];
    events.forEach((e) => {
      const m = ACTION_META[e.action] || { label: e.action };
      lines.push([
        e.created_at, m.label, e.registration || "", e.booking_reference || e.vda_reference || "",
        e.garage || e.customer || "", e.review_status || e.note || "", e.actor || "System",
        ROLE_LABEL[e.actor_role] || e.actor_role || "",
      ].map(esc).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `confirmation-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <StaffShell>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="vda-tick text-[hsl(var(--vda-quiet))]">Service / MOT · supervisor</p>
          <h1 className="text-[26px] font-semibold tracking-tight text-foreground mb-1">Confirmation audit</h1>
          <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
            A read-only trail of every confirmation received, matched, applied or rejected — who did it and when. Nothing here can be changed.
          </p>
        </div>
        <Btn variant="outline" data-testid="conf-audit-export" onClick={exportCsv} disabled={events.length === 0}>
          <Download className="h-4 w-4" /> Export CSV
        </Btn>
      </div>

      {/* Weekly throughput KPIs */}
      <div className="grid grid-cols-3 gap-3 mb-5" data-testid="conf-audit-kpis">
        {[
          { k: "received", label: "Received this week", v: kpi.received, tone: "text-foreground" },
          { k: "applied", label: "Applied this week", v: kpi.applied, tone: "text-primary" },
          { k: "rejected", label: "Rejected this week", v: kpi.rejected, tone: "text-[hsl(var(--vda-red))]" },
        ].map((m) => (
          <Panel key={m.k} className="p-4" data-testid={`conf-kpi-${m.k}`}>
            <span className="vda-tick text-[hsl(var(--vda-quiet))] block">{m.label}</span>
            <span className={`font-mono text-[26px] leading-none font-semibold tabular-nums ${m.tone}`}>{m.v}</span>
          </Panel>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Input data-testid="conf-audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search registration, ref, garage, customer or staff" />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">From
          <Input type="date" data-testid="conf-audit-from" value={from} onChange={(e) => setFrom(e.target.value)} className="w-auto" />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">To
          <Input type="date" data-testid="conf-audit-to" value={to} onChange={(e) => setTo(e.target.value)} className="w-auto" />
        </label>
        {(from || to) && <button data-testid="conf-audit-clear-range" onClick={() => { setFrom(""); setTo(""); }} className="text-xs text-primary hover:underline">Clear dates</button>}
        <div className="flex gap-2 flex-wrap" data-testid="conf-audit-action-filter">
          {[["", "All actions"], ...Object.entries(ACTION_META).map(([k, m]) => [k, m.label])].map(([v, l]) => (
            <button key={v || "all"} data-testid={`conf-audit-filter-${v || "all"}`} onClick={() => setAction(v)}
              className={`h-8 px-3 text-xs rounded-[6px] border transition-colors ${action === v ? "bg-primary text-primary-foreground border-primary" : "border-[hsl(var(--input))] bg-card hover:bg-secondary"}`}>{l}</button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : events.length === 0 ? (
        <EmptyState title="No confirmation activity yet" hint="Received, matched, applied and rejected confirmations will appear here." />
      ) : (
        <Panel className="overflow-hidden" data-testid="conf-audit-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="bg-secondary/60 text-left vda-tick text-muted-foreground">
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Action</th>
                  <th className="px-3 py-2 font-medium">Registration</th>
                  <th className="px-3 py-2 font-medium">Booking / ref</th>
                  <th className="px-3 py-2 font-medium">Garage / customer</th>
                  <th className="px-3 py-2 font-medium">Result</th>
                  <th className="px-3 py-2 font-medium">Staff</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => {
                  const m = ACTION_META[e.action] || { label: e.action, tone: "muted" };
                  return (
                    <tr key={e.id} className="border-t border-border" data-testid={`conf-audit-row-${e.id}`}>
                      <td className="px-3 py-2.5 font-mono text-[11px] text-[hsl(var(--vda-quiet))] whitespace-nowrap">{fmtDateTime(e.created_at)}</td>
                      <td className="px-3 py-2.5"><Tag tone={m.tone}>{m.label}</Tag></td>
                      <td className="px-3 py-2.5 vda-reg">{e.registration || "—"}</td>
                      <td className="px-3 py-2.5 vda-reg text-muted-foreground">{e.booking_reference || e.vda_reference || "—"}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{e.garage || e.customer || "—"}</td>
                      <td className="px-3 py-2.5">
                        {e.review_status ? <span className="text-xs text-muted-foreground">{e.review_status.replace(/_/g, " ")}</span>
                          : e.note ? <span className="text-xs text-muted-foreground">{e.note}</span> : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="leading-tight">{e.actor || "System"}</div>
                        {e.actor_role && <div className="text-[11px] text-muted-foreground">{ROLE_LABEL[e.actor_role] || e.actor_role}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </StaffShell>
  );
}
